import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiArrowRight, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import './About.css';

const teamMembers = [
  {
    id: 1,
    name: 'CA Vikas K. Jain',
    role: 'Technical',
    qualification: 'FCA',
    image: '/vikas-jain.png',
    linkedin: 'https://www.linkedin.com/in/vikas-jain-64966b60?utm_source=share_via&utm_content=profile&utm_medium=member_android',
    containImage: true,
    profile: `CA. Vikas K. Jain is an Associate Member of the Institute of Chartered Accountants of India (ICAI) with over 13 years of diversified professional experience across manufacturing, trading, pharmaceutical, service and dealership businesses.\n\nHe combines strong expertise in finance, taxation, compliance, business processes and management reporting with a practical understanding of the operational and financial challenges faced by growing businesses.\n\nHis experience spans financial reporting, direct and indirect taxation, regulatory compliance, financial analysis, MIS and business advisory, enabling him to provide integrated solutions beyond traditional accounting and compliance services.\n\nVikas currently works closely with MSMEs and corporates as a business and finance growth partner, providing retainer-based advisory and managing end-to-end financial and compliance requirements.\n\nAt DBH, his experience contributes to the development and implementation of data-driven financial intelligence, analytics, MIS, compliance and process-automation solutions.`
  },
  {
    id: 2,
    name: 'Alister Fernandes',
    role: 'Technical',
    qualification: 'Bachelor of Engineering',
    image: '/alister.jpeg',
    linkedin: 'https://www.linkedin.com/in/alister1210/',
    profile: `B.E. in IT from St. Francis Institute of Technology, Mumbai (CGPA 8.2). Skilled in Full Stack Development, AI/ML, NLP, Data Processing, and Predictive Analytics, with experience building AI Call Center CRM and automated document processing systems.`
  },
  {
    id: 3,
    name: 'Paresh Sharma',
    role: 'Technical',
    qualification: 'B.Voc (AI & DS)',
    image: '/paresh.jpeg',
    linkedin: 'https://www.linkedin.com/in/paresh-sharma-40377a253/'
  },
  {
    id: 4,
    name: 'Nihant Lohar',
    role: 'Technical',
    qualification: 'B.Voc (AI & DS)',
    image: '/nishant.jpeg',
    linkedin: 'https://www.linkedin.com/in/nishant-lohar-233779253/'
  },
  {
    id: 5,
    name: 'Varun Thakkar',
    role: 'Technical',
    qualification: 'B.Voc (AI & DS)',
    image: '/varun.jpeg',
    linkedin: 'https://www.linkedin.com/in/varun-thakkar-178969326/'
  },
  {
    id: 6,
    name: 'Mahika Chaurasiya',
    role: 'Technical',
    qualification: 'Intern – IT',
    image: '/mahika-img.jpeg',
    linkedin: 'https://www.linkedin.com/in/mahika-chaurasiya-17910530a/'
  },
  {
    id: 7,
    name: 'Ram Kumar Chaurasiya',
    role: 'Technical',
    qualification: 'Intern – (AI & DS)',
    image: '/ramkumar.png',
    linkedin: 'https://www.linkedin.com/in/ramkumar-chaurasiya-7544ab30b/'
  },
  {
    id: 8,
    name: 'Smit Sakariya',
    role: 'Technical',
    qualification: 'Intern – Data Science',
    image: '/smit.jpeg',
    linkedin: 'https://www.linkedin.com/in/smitsakariya16/'
  }
];

const CAROUSEL_CARD_W = 300;
const CAROUSEL_GAP = 28;
const CAROUSEL_SPEED = 0.35;

const About = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const trackRef = useRef(null);
  const posRef = useRef(0);
  const pausedRef = useRef(false);
  const rafRef = useRef(null);
  const dragRef = useRef({ active: false, startX: 0, scrollLeft: 0, moved: false });
  const touchRef = useRef({ active: false, startX: 0, moved: false });
  const [flippedCards, setFlippedCards] = useState({});

  const singleSetW = teamMembers.length * (CAROUSEL_CARD_W + CAROUSEL_GAP);

  const tick = useCallback(() => {
    if (!pausedRef.current && trackRef.current) {
      posRef.current += CAROUSEL_SPEED;
      if (posRef.current >= singleSetW) posRef.current -= singleSetW;
      trackRef.current.style.transform = `translate3d(-${posRef.current}px,0,0)`;
    }
    rafRef.current = requestAnimationFrame(tick);
  }, [singleSetW]);

  useEffect(() => {
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [tick]);

  const pauseCarousel = () => { pausedRef.current = true; };
  const resumeCarousel = () => { if (!dragRef.current.active) pausedRef.current = false; };

  const handleCardClick = (id) => {
    if (dragRef.current.moved || touchRef.current.moved) return;
    setFlippedCards(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleCardHover = (id, entering) => {
    if (dragRef.current.moved) return;
    setFlippedCards(prev => ({ ...prev, [id]: entering }));
  };

  const handleMouseDown = (e) => {
    dragRef.current = { active: true, startX: e.pageX, scrollLeft: posRef.current, moved: false };
    pausedRef.current = true;
    if (trackRef.current) trackRef.current.style.cursor = 'grabbing';
  };

  const handleMouseMove = (e) => {
    if (!dragRef.current.active) return;
    e.preventDefault();
    const dx = e.pageX - dragRef.current.startX;
    if (Math.abs(dx) > 3) dragRef.current.moved = true;
    posRef.current = dragRef.current.scrollLeft - dx;
    if (posRef.current < 0) posRef.current += singleSetW;
    if (posRef.current >= singleSetW) posRef.current -= singleSetW;
    if (trackRef.current) trackRef.current.style.transform = `translate3d(-${posRef.current}px,0,0)`;
  };

  const handleMouseUp = () => {
    dragRef.current.active = false;
    pausedRef.current = false;
    if (trackRef.current) trackRef.current.style.cursor = 'grab';
  };

  const handleTouchStart = (e) => {
    touchRef.current = { active: true, startX: e.touches[0].clientX, moved: false };
    pausedRef.current = true;
  };

  const handleTouchMove = (e) => {
    if (!touchRef.current.active) return;
    const dx = e.touches[0].clientX - touchRef.current.startX;
    if (Math.abs(dx) > 8) touchRef.current.moved = true;
    posRef.current -= dx;
    touchRef.current.startX = e.touches[0].clientX;
    if (posRef.current < 0) posRef.current += singleSetW;
    if (posRef.current >= singleSetW) posRef.current -= singleSetW;
    if (trackRef.current) trackRef.current.style.transform = `translate3d(-${posRef.current}px,0,0)`;
  };

  const handleTouchEnd = () => {
    touchRef.current.active = false;
    pausedRef.current = false;
  };

  const scrollByCards = (dir) => {
    const shift = (CAROUSEL_CARD_W + CAROUSEL_GAP) * dir;
    posRef.current = ((posRef.current + shift) % singleSetW + singleSetW) % singleSetW;
    if (trackRef.current) trackRef.current.style.transform = `translate3d(-${posRef.current}px,0,0)`;
  };

  const carouselItems = [...teamMembers, ...teamMembers];

  return (
    <div className="about-page">
      <div className="about-flowbox-card">
        
        <motion.div 
          className="pro-about-hero"
          style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/about-bg.png)` }}
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.6 }}
        >
          <div className="pro-about-hero-left">
            <span className="g-eyebrow">ABOUT US</span>
            <p className="pro-about-desc">
              At Data Bolta Hai, we bring together industry expertise, advanced technology, data analytics, and mathematical intelligence to transform complex data into meaningful business insights. Our solutions help organisations improve processes, identify opportunities, and make confident, data-driven decisions.
            </p>
            <p className="pro-about-desc">
              From financial and business analytics to process optimisation and customised data solutions, we focus on delivering practical outcomes tailored to each client's needs. Transparency, confidentiality, accuracy, and integrity remain at the core of every engagement we undertake.
            </p>
            <Link to="/services" className="about-hero-cta" style={{ alignSelf: 'flex-start', marginTop: '16px' }}>
              Discover Our Approach 
              <span className="about-hero-cta-icon">
                <FiArrowRight />
              </span>
            </Link>
          </div>
          
          <div className="pro-about-hero-right">
            <img src="/about-svg.png" alt="About SVG" className="about-hero-svg" />
          </div>
        </motion.div>

        {/* Mission and Vision Section */}
        <section className="mv-section">
          <div className="mv-container">
            <motion.div
              className="mv-header"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="mv-title">Our Mission & Vision</h2>
              <p className="mv-intro">
                A clear purpose and a forward-looking approach to turning data into meaningful business impact.
              </p>
            </motion.div>

            <div className="mv-divider-line" />

            <motion.div
              className="mv-card"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.15 }}
            >
              <div className="mv-columns">
                <div className="mv-col">
                  <div className="mv-img-wrap">
                    <img src="/mission.png" alt="Our Mission" className="mv-img" />
                  </div>
                  <h3 className="mv-col-heading">Mission</h3>
                  <p className="mv-col-text">
                    Our mission is to empower organizations to unlock the full potential of their data through innovative technology. We turn raw data into actionable insights that drive growth, smarter decision-making, and greater efficiency.
                  </p>
                </div>

                <div className="mv-center-divider" />

                <div className="mv-col">
                  <div className="mv-img-wrap">
                    <img src="/vision.png" alt="Our Vision" className="mv-img" />
                  </div>
                  <h3 className="mv-col-heading">Vision</h3>
                  <p className="mv-col-text">
                    We aspire to become a global leader in data analytics, delivering innovative solutions that help organizations leverage data effectively, drive growth, and make smarter decisions. Our vision is to shape a future where data-driven decisions define success across industries.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Subtle decorative background shapes */}
          <div className="mv-bg-decor" aria-hidden="true">
            <svg width="200" height="200" viewBox="0 0 200 200" fill="none">
              <circle cx="100" cy="100" r="90" stroke="#037eb7" strokeWidth="0.5" opacity="0.06" />
              <circle cx="100" cy="100" r="60" stroke="#037eb7" strokeWidth="0.5" opacity="0.04" />
            </svg>
          </div>
          <div className="mv-bg-decor mv-bg-decor--right" aria-hidden="true">
            <svg width="160" height="160" viewBox="0 0 160 160" fill="none">
              <circle cx="80" cy="80" r="70" stroke="#2e3f51" strokeWidth="0.5" opacity="0.05" />
              <circle cx="80" cy="80" r="45" stroke="#2e3f51" strokeWidth="0.5" opacity="0.03" />
            </svg>
          </div>
        </section>

        {/* Premium Mentors Section */}
        <div className="premium-mentors-section" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/image.png)` }}>
          <div className="p-mentors-container">
            
            {/* Header */}
            <motion.div 
              className="p-mentors-header"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="p-mentors-eyebrow">OUR EXPERTS</span>
              <h2 className="p-mentors-title">THE EXPERTISE BEHIND <span>Data Bolta Hai</span></h2>
            </motion.div>

            <div className="mv-divider-line" />

            {/* Card */}
            <a 
              href="https://www.linkedin.com/in/amit-heda-03014919/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="p-mentors-card-link"
            >
              <motion.div
                className="p-mentors-card"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.15 }}
              >
                <div className="p-mentors-card-left">
                  <div className="p-mentor-badge">MEET OUR MENTOR</div>
                  <h3 className="p-mentor-name">Amit Kumar Heda</h3>
                  <h4 className="p-mentor-designation">CEO & FOUNDER | FCA, CS, FAFD</h4>
                  <div className="p-mentor-accent-line"></div>
                  
                  <p className="p-mentor-bio">
                    Amit Kumar Heda (FCA, CS, FAFD) is the CEO of Relax Exim LLP and a fellow member of the Institute of Chartered Accountants of India. He has completed Company Secretary from ICSI, a Certificate Course on Forensic Accounting and Fraud Detection, and 'A' Level from DOEACC, New Delhi. With strong knowledge of C, C++ &amp; SQL programming languages, he has hands-on working experience in various advanced tools including R, Python and ACL for Data Mining/Analysis. He is the founder of the firm with over 15 years of experience in almost every aspect of the accounting and auditing profession, with expertise in Data Mining using various advanced tools.
                  </p>
                  <p className="p-mentor-bio">
                    A dynamic, go-getter, visionary and far-sighted professional, he has his sight set on steering this firm to the pinnacle of glory by providing end-to-end solutions under various revenue and allied laws under one umbrella. He has also participated in various seminars and conferences organized by the ICAI and other related professional institutions.
                  </p>
                </div>

                <div className="p-mentors-card-right">
                  <div className="p-portrait-wrapper">
                    <div className="p-decor-bg-rect"></div>
                    <div className="p-decor-dots"></div>
                    <div className="p-decor-circle"></div>
                    <div className="p-decor-edge-bar"></div>
                    <div className="p-portrait-card">
                      <img src="/amit.jpg" alt="Amit Kumar Heda" />
                    </div>
                  </div>
                </div>
              </motion.div>
            </a>
          </div>
        </div>

        {/* Team Section Divider */}
        <div className="team-section-divider"></div>

        {/* Our Team Section */}
        <div className="team-section">
          <div className="t-decor-dots"></div>
          <div className="t-decor-circle"></div>
          <div className="t-decor-line"></div>

          <div className="team-container">
            <motion.div
              className="team-header"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="team-eyebrow">OUR TEAM</span>
              <h2 className="team-title">THE PEOPLE BEHIND <span>Data Bolta Hai</span></h2>
              <p className="team-subtitle">Meet the professionals and technical talent driving data, technology and innovation at DBH.</p>
            </motion.div>

            {/* Carousel */}
            <div
              className="tc-carousel"
              onMouseEnter={pauseCarousel}
              onMouseLeave={resumeCarousel}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeaveCapture={handleMouseUp}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <button className="tc-arrow tc-arrow--left" onClick={() => scrollByCards(-1)} aria-label="Previous">
                <FiChevronLeft />
              </button>
              <button className="tc-arrow tc-arrow--right" onClick={() => scrollByCards(1)} aria-label="Next">
                <FiChevronRight />
              </button>

              <div className="tc-viewport">
                <div className="tc-track" ref={trackRef}>
                  {carouselItems.map((member, i) => (
                    <div
                      className="tc-card-wrap"
                      key={`carousel-${member.id}-${i}`}
                      style={{ width: CAROUSEL_CARD_W }}
                    >
                      <div
                        className={`tc-flip-card ${flippedCards[member.id] ? 'flipped' : ''}`}
                        onMouseEnter={() => handleCardHover(member.id, true)}
                        onMouseLeave={() => handleCardHover(member.id, false)}
                        onClick={() => handleCardClick(member.id)}
                      >
                        {/* Front */}
                        <div className="tc-card-face tc-card-front">
                          <div className={`tc-card-img-wrap ${member.containImage ? 'tc-img-contain' : ''}`}>
                            <img src={member.image} alt={member.name} draggable="false" />
                          </div>
                          <div className="tc-card-info">
                            <h3 className="tc-card-name">{member.name}</h3>
                            <p className="tc-card-role">{member.role}</p>
                            <p className="tc-card-qual">{member.qualification}</p>
                          </div>
                        </div>
                        {/* Back */}
                        <div className="tc-card-face tc-card-back">
                          {member.profile ? (
                            <div className="tc-back-content tc-back-with-photo">
                              <div className="tc-back-header">
                                <div className="tc-back-avatar">
                                  <img src={member.image} alt={member.name} draggable="false" />
                                </div>
                                <div className="tc-back-header-info">
                                  <h3 className="tc-back-name">{member.name}</h3>
                                  <p className="tc-back-role">{member.qualification}</p>
                                </div>
                              </div>
                              <p className="tc-back-text">{member.profile.split('\n\n')[0]}</p>
                              {member.linkedin && (
                                <a
                                  href={member.linkedin}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="tc-back-link"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  View LinkedIn Profile
                                </a>
                              )}
                            </div>
                          ) : (
                            <div className="tc-back-content tc-back-centered">
                              <div className="tc-back-avatar">
                                <img src={member.image} alt={member.name} draggable="false" />
                              </div>
                              <h3 className="tc-back-name">{member.name}</h3>
                              <p className="tc-back-role">{member.qualification}</p>
                              {member.linkedin && (
                                <a
                                  href={member.linkedin}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="tc-back-link"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  View LinkedIn Profile
                                </a>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
